"use client";

import { PORTALS } from ".constants/portals";
import {
  PortalModal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  Button,
  usePortalContext,
  ButtonSetSlide,
  Icon,
  Badge,
  cn,
} from "@cloai/uikit";
import { motion } from "framer-motion";
import { map } from "lodash";
import { useLocale, useTranslations } from "next-intl";
import { memo, useCallback, useMemo, useState } from "react";

import {
  PhotoSettings,
  useABtestContext,
  useUndressContext,
  useUndressSettingsContext,
} from "@/providers";
import { getLocalizedName } from "@/utils/getLocalizedName";

import { ItemCard } from "./components/ItemCard";
import { STYLES as STYLES_V1 } from "./styles";
import { STYLES as STYLES_V2 } from "./stylesV2";
import { MOTION_VARIANTS } from "./vars";

export const CollectionsPortal = memo(() => {
  const t = useTranslations("UserFlowModal");
  const s = useTranslations("HomePage.settingsPopup");
  const {
    clickFileId,
    clickUndressingFile,
    onReUseFaceObj,
    reUseFaceObj,
    updatePhotoSetting,
    selectedCollectionCount,
  } = useUndressContext();
  const { sets, storylines } = useUndressSettingsContext();
  const { closePortal, openPortal } = usePortalContext();
  const { AAB_RedesignV1 } = useABtestContext();

  const STYLES = AAB_RedesignV1 ? STYLES_V1 : STYLES_V2;

  const locale = useLocale();

  const [tab, setTab] = useState(0);

  const list = useMemo(
    () => (tab ? storylines : sets),
    [tab, sets, storylines],
  );

  const handleClick = useCallback(
    (itemType: keyof PhotoSettings, itemId: string) => async () => {
      updatePhotoSetting(clickFileId, itemType, itemId);
      closePortal();

      if (reUseFaceObj) {
        return onReUseFaceObj();
      }

      if (!clickUndressingFile) {
        return openPortal(PORTALS.USER_FLOW_UPLOAD);
      }
    },
    [
      clickFileId,
      clickUndressingFile,
      closePortal,
      onReUseFaceObj,
      openPortal,
      reUseFaceObj,
      updatePhotoSetting,
    ],
  );

  return (
    <PortalModal
      portalId={PORTALS.USER_FLOW_COLLECTIONS}
      style={STYLES.portal.variants.wide}
    >
      <ModalHeader>{t("titles.chooseCollection")}</ModalHeader>

      <ModalContent className="@container">
        <motion.div
          variants={MOTION_VARIANTS.container}
          initial="hidden"
          animate="visible"
          className="flex flex-col gap-popup-gap"
        >
          <motion.span
            key={`subtitle-${tab}`}
            variants={MOTION_VARIANTS.item}
            className="flex items-center justify-center gap-1 text-center text-balance opacity-80"
          >
            <Icon icon={tab ? "cards_star" : "filter"} />
            {tab
              ? s("settingsStorylinesNote")
              : s("settingsSetsNote", { count: selectedCollectionCount || 9 })}
          </motion.span>

          <div
            role="grid"
            aria-label="Collections grid"
            className={cn(STYLES.grid.variants.big, STYLES.grid.animate)}
          >
            {map(list, (item, Index) => (
              <motion.div
                key={item.id}
                variants={MOTION_VARIANTS.item}
                custom={Index}
                role="gridcell"
                aria-label={getLocalizedName(item, locale)}
              >
                <ItemCard
                  item={item}
                  index={Index}
                  onClick={handleClick(tab ? "storyline" : "set", item.id)}
                  variant="wide"
                />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </ModalContent>

      <ModalFooter>
        <div className={cn("w-full", AAB_RedesignV1 && "mx-auto max-w-110")}>
          <ButtonSetSlide activeButton={tab} wide>
            <Button
              startContent={<Icon icon="filter" size={5} />}
              variant="light"
              size="md"
              wide
              centerContent
              className="rounded-xl"
              onClick={() => setTab(0)}
            >
              {s("settingsSets")}
            </Button>

            <Badge
              content="NEW"
              color="secondary"
              size="lg"
              variant="shadow"
              wide
            >
              <Button
                startContent={<Icon icon="cards_star" size={5} />}
                variant="light"
                size="md"
                wide
                centerContent
                className="rounded-xl"
                onClick={() => setTab(1)}
              >
                {s("settingsStorylines")}
              </Button>
            </Badge>
          </ButtonSetSlide>
        </div>
      </ModalFooter>
    </PortalModal>
  );
});

CollectionsPortal.displayName = "CollectionsPortal";
